/**
 * State Mapping Configuration
 *
 * Maps humanitarian GeoJSON shapeName properties to application stateCode format.
 * Handles both standard English names and diacritical mark versions from
 * authentic boundary datasets.
 *
 * @author TRAC India Team
 * @version 1.0.0
 */

// Mapping between humanitarian data shapeName and application stateCode
export const shapeNameToStateCode = {
  // Standard English names (as used in app state codes)
  "Andhra Pradesh": "andhra-pradesh",
  "Arunachal Pradesh": "arunachal-pradesh",
  Assam: "assam",
  Bihar: "bihar",
  Chhattisgarh: "chhattisgarh",
  Goa: "goa",
  Gujarat: "gujarat",
  Haryana: "haryana",
  "Himachal Pradesh": "himachal-pradesh",
  Jharkhand: "jharkhand",
  Karnataka: "karnataka",
  Kerala: "kerala",
  "Madhya Pradesh": "madhya-pradesh",
  Maharashtra: "maharashtra",
  Manipur: "manipur",
  Meghalaya: "meghalaya",
  Mizoram: "mizoram",
  Nagaland: "nagaland",
  Odisha: "odisha",
  Punjab: "punjab",
  Rajasthan: "rajasthan",
  Sikkim: "sikkim",
  "Tamil Nadu": "tamil-nadu",
  Telangana: "telangana",
  Tripura: "tripura",
  "Uttar Pradesh": "uttar-pradesh",
  Uttarakhand: "uttarakhand",
  "West Bengal": "west-bengal",
  Delhi: "delhi",
  Chandigarh: "chandigarh",
  "Andaman and Nicobar Islands": "andaman-nicobar",
  "Jammu and Kashmir": "jammu-kashmir",
  Puducherry: "puducherry",
  Ladakh: "ladakh",
  Lakshadweep: "lakshadweep",

  // Legacy topojson variants (from india.topo.json ST_NM values)
  "Andaman & Nicobar Island": "andaman-nicobar",
  "Jammu & Kashmir": "jammu-kashmir",
  "Dadara & Nagar Havelli": "dadra-nagar-haveli",
  "Daman & Diu": "daman-diu",

  // Diacritical mark versions from authentic GeoJSON
  Mahārāshtra: "maharashtra",
  Nāgāland: "nagaland",
  "Tamil Nādu": "tamil-nadu",
  Chhattīsgarh: "chhattisgarh",
  Gujarāt: "gujarat",
  Bihār: "bihar",
  Ladākh: "ladakh",
  Rājasthān: "rajasthan",
  Telangāna: "telangana",
  "Arunāchal Pradesh": "arunachal-pradesh",
  Meghālaya: "meghalaya",
  Jhārkhand: "jharkhand",
  Karnātaka: "karnataka",
  Uttarākhand: "uttarakhand",
  "Dādra and Nagar Haveli and Damān and Diu": "dadra-nagar-haveli-daman-diu",
  "Dadra and Nagar Haveli and Daman and Diu": "dadra-nagar-haveli-daman-diu",
  Chandīgarh: "chandigarh",
  "Himāchal Pradesh": "himachal-pradesh",
  "Jammu and Kashmīr": "jammu-kashmir",
  Haryāna: "haryana",

  // Common misspellings and variants found in data files
  Gujrat: "gujarat",
  Tamilnadu: "tamil-nadu",
  "Arunanchal Pradesh": "arunachal-pradesh",
  Orissa: "odisha",
  Pondicherry: "puducherry",
  Uttaranchal: "uttarakhand",
  "Andaman and Nicobar": "andaman-nicobar",
  "Andaman & Nicobar": "andaman-nicobar",
  "NCT of Delhi": "delhi",
  "NCT Delhi": "delhi",

  // Old or alternate UT names
  "Dadra and Nagar Haveli": "dadra-nagar-haveli",
  "Dadra & Nagar Haveli": "dadra-nagar-haveli",
  "Daman and Diu": "daman-diu",
  "D & N Haveli": "dadra-nagar-haveli",
  DNH: "dadra-nagar-haveli",
  "A & N Islands": "andaman-nicobar",
};

/**
 * Processes GeoJSON features from humanitarian datasets for React-Leaflet compatibility
 *
 * @param {Object} feature - Raw GeoJSON feature from humanitarian boundary data
 * @returns {Object} Processed feature with standardized properties for map rendering
 *
 * Transforms:
 * - shapeName (with diacritics) → clean display name + stateCode
 * - Ensures compatibility with the map/table components
 * - Handles fallback cases for unmapped state names
 */
export const processGeoJSONFeature = (feature) => {
  const shapeName = feature.properties.shapeName;
  const stateCode = shapeNameToStateCode[shapeName];

  // Create a clean display name (remove diacritics for display)
  const cleanName = shapeName
    .replace(/ā/g, "a")
    .replace(/ī/g, "i")
    .replace(/ū/g, "u")
    .replace(/ē/g, "e")
    .replace(/ō/g, "o")
    .replace(/ṅ/g, "n")
    .replace(/ṃ/g, "m")
    .replace(/ḥ/g, "h");

  // Add the expected properties for the map component
  return {
    ...feature,
    properties: {
      ...feature.properties,
      name: cleanName, // Clean display name
      stateCode:
        stateCode ||
        shapeName
          .toLowerCase()
          .replace(/\s+/g, "-")
          .replace(/[āīūēōṅṃḥ]/g, (match) => {
            const map = {
              ā: "a",
              ī: "i",
              ū: "u",
              ē: "e",
              ō: "o",
              ṅ: "n",
              ṃ: "m",
              ḥ: "h",
            };
            return map[match] || match;
          }), // Fallback for missing mappings
    },
  };
};
